// ---------------- EmailContestar.jsx -----------------

import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  Select,
  MenuItem,
  Chip,
  Typography,
  CircularProgress,
  Collapse,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import DropZoneWrapper from "../dropwrapper/DropZoneWrapper";
import RecipientField from "../RecipientField";
import {
  resolveSignatureHtmlForCompose,
  signatureHtmlForSend,
} from "../signatureHtml";
import { resolveHtmlInlineImagesForDisplay } from "../embedCidInHtml";
import {
  composeDialogSlotProps,
  guardComposeDialogClose,
  hasComposeDraft,
  ComposeDiscardConfirmDialog,
} from "../composeDialogUi";
import TipTapEditorContestar from "./TipTapEditorContestar";
import axios from "axios";
import { validateRecipientField } from "../emailRecipients";
import {
  getReplyFromEmail,
  getReplyToAddress,
  getReplyCcAddresses,
} from "../whatsappEmail/threadGrouping";
import { emailMessagePath } from "../emailApiPaths";
import { looksLikeTruncatedQuotedBody } from "../truncatedBodyHeuristic";
import { safeSetEditorHtml } from "../tiptapExtensions";

const API = process.env.REACT_APP_API_URL;

const ACCENT = "#00bbcd";
const ACCENT_HOVER = "#00a5b5";
const ON_ACCENT = "#0f172a";

const accentBtnSx = {
  bgcolor: ACCENT,
  color: ON_ACCENT,
  fontWeight: 600,
  boxShadow: "none",
  "&:hover": { bgcolor: ACCENT_HOVER, boxShadow: "none" },
};

const accentOutlinedSx = {
  color: ACCENT,
  borderColor: ACCENT,
  "&:hover": {
    borderColor: ACCENT_HOVER,
    bgcolor: "rgba(0, 187, 205, 0.08)",
  },
};

const accentTextSx = {
  color: ACCENT,
  "&:hover": { bgcolor: "rgba(0, 187, 205, 0.08)" },
};

export default function EmailContestar({
  open,
  onClose,
  onSend,
  email,
  accounts,
  originalHtml,
  headerMeta = {},
}) {
  const editorRef = useRef(null);
  const quotedHtmlRef = useRef("");
  const draftBaselineRef = useRef(null);

  const [discardOpen, setDiscardOpen] = useState(false);

  const [fromEmail, setFromEmail] = useState("");
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState("");

  const [selectedSignature, setSelectedSignature] = useState("reply");
  const [signatures, setSignatures] = useState({ main: "", reply: "", logo_path: null });

  const [attachments, setAttachments] = useState([]);
  const [initialHtml, setInitialHtml] = useState("");
  const [quotedHtml, setQuotedHtml] = useState("");
  const [resolvedOriginalHtml, setResolvedOriginalHtml] = useState("");
  const [loadingOriginal, setLoadingOriginal] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const accountOptions = (accounts || [])
    .map((a) => ({
      id: a?.id,
      email: String(a?.email || "").trim(),
      value: String(a?.email || "").trim().toLowerCase(),
    }))
    .filter((a) => a.value);

  // Nunca pasar value="" al Select si hay cuentas: MUI out-of-range → pantalla en blanco.
  const selectFromEmail = accountOptions.some((a) => a.value === fromEmail)
    ? fromEmail
    : accountOptions[0]?.value || "";

  const buildQuotedBlock = (html) => {
    if (!html || !email) return "";
    return `
      <br/><br/>
      ---------- Mensaje original ----------
      <br/><b>De:</b> ${email.from}<br/>
      <b>Fecha:</b> ${new Date(email.date).toLocaleString("es-ES")}<br/>
      <b>Asunto:</b> ${email.subject}<br/><br/>
      ${html}
    `;
  };

  const closeDialogSafely = () => {
    onClose?.();
  };

  const requestClose = () => {
    const baseline = draftBaselineRef.current || {};
    if (
      hasComposeDraft({
        to,
        cc,
        bcc,
        subject,
        html: editorRef.current?.getHTML?.() ?? initialHtml,
        attachments,
        baseline,
      })
    ) {
      setDiscardOpen(true);
      return;
    }
    closeDialogSafely();
  };

  const confirmDiscard = () => {
    setDiscardOpen(false);
    closeDialogSafely();
  };

  useEffect(() => {
    if (!open) {
      draftBaselineRef.current = null;
    }
  }, [open]);

  /* -----------------------------------------------------
        RESET AL ABRIR
  ----------------------------------------------------- */
  useEffect(() => {
    if (!open || !email) return;

    setAttachments([]);

    const replyTo = getReplyToAddress(email, accounts);
    setTo(replyTo || email.from || "");
    const replyCc = getReplyCcAddresses(email, accounts, headerMeta);
    setCc(replyCc);
    setBcc("");
    setShowCc(!!String(replyCc || "").trim());
    setShowBcc(false);
    setSubject(`Re: ${email.subject || ""}`.replace(/^Re:\s*Re:\s*/i, "Re: "));

    const replyFrom = getReplyFromEmail(email, accounts);
    const options = (accounts || [])
      .map((a) => String(a?.email || "").trim().toLowerCase())
      .filter(Boolean);
    const safeFrom =
      (replyFrom && options.includes(replyFrom) && replyFrom) ||
      options[0] ||
      "";
    setFromEmail(safeFrom);

    draftBaselineRef.current = {
      ...(draftBaselineRef.current || {}),
      to: replyTo || email.from || "",
      cc: replyCc || "",
      bcc: "",
      subject: `Re: ${email.subject || ""}`.replace(/^Re:\s*Re:\s*/i, "Re: "),
    };
  }, [open, email, accounts, headerMeta]);

  /* -----------------------------------------------------
        CARGAR FIRMAS
  ----------------------------------------------------- */
  useEffect(() => {
    if (!open) return;
    const emailForSig = fromEmail || selectFromEmail;
    if (!emailForSig) return;

    axios
      .get(`${API}/signatures/${emailForSig}`)
      .then((res) => {
        const main = res.data.signature_html || "";
        const reply = res.data.reply_signature_html || main;
        setSignatures({
          main,
          reply,
          logo_path: res.data.logo_path || null,
        });
      })
      .catch((err) => {
        console.warn("⚠️ firmas respuesta:", err);
        setSignatures({ main: "", reply: "", logo_path: null });
      });
  }, [open, fromEmail, selectFromEmail]);

  /* Cuerpo original: caché del padre o fetch si falta */
  useEffect(() => {
    if (!open || !email?.gmailId) {
      setResolvedOriginalHtml("");
      setQuotedHtml("");
      setLoadingOriginal(false);
      return;
    }

    const cached = originalHtml || "";
    const cachedOk =
      cached &&
      !cached.includes("Vista previa del índice") &&
      !looksLikeTruncatedQuotedBody(cached);

    if (cachedOk) {
      setResolvedOriginalHtml(cached);
      setLoadingOriginal(false);
      return;
    }

    let cancelled = false;
    setLoadingOriginal(true);

    (async () => {
      try {
        let html = "";
        for (let attempt = 0; attempt < 3; attempt++) {
          const url =
            attempt > 0
              ? `${emailMessagePath(API, email.gmailId, "body")}?_ts=${Date.now()}`
              : emailMessagePath(API, email.gmailId, "body");
          const res = await axios.get(url, { timeout: 45_000 });
          html = res.data.html || res.data.text || "";
          const usable =
            html &&
            !res.data.partial &&
            !looksLikeTruncatedQuotedBody(html);
          if (usable) break;
          if (attempt + 1 < 3) {
            await new Promise((r) => setTimeout(r, 2000 + attempt * 1500));
          }
        }
        if (!cancelled) {
          const usable = html && !looksLikeTruncatedQuotedBody(html);
          setResolvedOriginalHtml(usable ? html : "");
        }
      } catch (err) {
        console.error("Error cargando cuerpo para responder:", err);
        if (!cancelled) setResolvedOriginalHtml("");
      } finally {
        if (!cancelled) setLoadingOriginal(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, email, originalHtml]);

  /* Bloque citado: ref con cid intacto para envío; UI con imágenes resueltas */
  useEffect(() => {
    if (!open || !resolvedOriginalHtml || !email) {
      setQuotedHtml("");
      quotedHtmlRef.current = "";
      return;
    }

    quotedHtmlRef.current = buildQuotedBlock(resolvedOriginalHtml);

    let cancelled = false;
    (async () => {
      const forDisplay = await resolveHtmlInlineImagesForDisplay(
        API,
        email.gmailId,
        resolvedOriginalHtml
      );
      if (!cancelled) {
        setQuotedHtml(buildQuotedBlock(forDisplay));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, resolvedOriginalHtml, email]);

  /* Editor: respuesta editable + firma; el mensaje original no pasa por TipTap */
  useEffect(() => {
    if (!open) return;

    const signatureHTML = resolveSignatureHtmlForCompose(
      selectedSignature === "main"
        ? signatures.main
        : selectedSignature === "reply"
        ? signatures.reply
        : "",
      signatures.logo_path,
      API
    );

    const content = `<p></p>${signatureHTML}`;
    draftBaselineRef.current = {
      ...(draftBaselineRef.current || {}),
      html: content,
    };

    const ed = editorRef.current;
    if (!ed || ed.isDestroyed) {
      setInitialHtml(content);
      return;
    }
    safeSetEditorHtml(ed, content);
  }, [open, signatures, selectedSignature]);

  /* -----------------------------------------------------
        MANEJO DE NUEVOS ARCHIVOS
  ----------------------------------------------------- */
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments((prev) => [...prev, ...files]);
  };

  const removeAttachment = (name) => {
    setAttachments((prev) => prev.filter((f) => f.name !== name));
  };

  /* -----------------------------------------------------
        ENVIAR RESPUESTA
  ----------------------------------------------------- */
  const sendReply = async () => {
    if (isSending) return;
    const sendAs = fromEmail || selectFromEmail;
    if (!sendAs) {
      alert("Selecciona la cuenta desde la que responder.");
      return;
    }
    if (!to.trim()) {
      alert("Debe indicar un destinatario.");
      return;
    }

    const recipients = validateRecipientField(to);
    if (!recipients.ok) {
      alert(`❌ ${recipients.message}`);
      return;
    }
    const ccCheck = validateRecipientField(cc, { required: false });
    if (!ccCheck.ok) {
      alert(`❌ ${ccCheck.message}`);
      return;
    }
    const bccCheck = validateRecipientField(bcc, { required: false });
    if (!bccCheck.ok) {
      alert(`❌ ${bccCheck.message}`);
      return;
    }

    const userHtml = signatureHtmlForSend(
      editorRef.current?.getHTML?.() || "",
      signatures.logo_path,
      API
    );
    const html = `${userHtml}${quotedHtmlRef.current || ""}`;

    const form = new FormData();
    form.append("fromEmail", sendAs);
    form.append("to", recipients.normalized);
    form.append("cc", ccCheck.normalized);
    form.append("bcc", bccCheck.normalized);
    form.append("subject", subject);
    form.append("html", html);
    form.append("isReply", "true");
    form.append("skipServerSignature", "true");
    if (email?.gmailId) {
      form.append("replySourceGmailId", email.gmailId);
    }

    attachments.forEach((file) => form.append("attachments", file));

    setIsSending(true);
    try {
      await onSend(form);
      closeDialogSafely();
    } catch (err) {
      console.error("Error enviando respuesta:", err);
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Error enviando la respuesta";
      alert(`❌ ${msg}`);
    } finally {
      setIsSending(false);
    }
  };

  /* -----------------------------------------------------
        UI
  ----------------------------------------------------- */
  return (
    <>
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={isSending ? undefined : guardComposeDialogClose(requestClose)}
      disableEscapeKeyDown
      slotProps={composeDialogSlotProps}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
        Responder mensaje
        <IconButton onClick={requestClose} disabled={isSending}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {/* FROM */}
        <Select
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          displayEmpty
          value={selectFromEmail}
          onChange={(e) => setFromEmail(e.target.value)}
        >
          {!accountOptions.length ? (
            <MenuItem value="" disabled>
              Sin cuentas
            </MenuItem>
          ) : null}
          {accountOptions.map((acc) => (
            <MenuItem key={acc.id || acc.value} value={acc.value}>
              {acc.email}
            </MenuItem>
          ))}
        </Select>

        {/* PARA */}
        <RecipientField
          label="Para"
          value={to}
          onChange={setTo}
          disabled={isSending}
          sx={{ mb: 1 }}
        />

        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
          <Button
            size="small"
            onClick={() => setShowCc((x) => !x)}
            endIcon={<KeyboardArrowDownIcon />}
            sx={accentTextSx}
          >
            CC
          </Button>
          <Button
            size="small"
            onClick={() => setShowBcc((x) => !x)}
            endIcon={<KeyboardArrowDownIcon />}
            sx={accentTextSx}
          >
            CCO
          </Button>
        </Box>

        <Collapse in={showCc}>
          <RecipientField
            label="CC"
            value={cc}
            onChange={setCc}
            disabled={isSending}
          />
        </Collapse>

        <Collapse in={showBcc}>
          <RecipientField
            label="CCO"
            value={bcc}
            onChange={setBcc}
            disabled={isSending}
          />
        </Collapse>

        {/* ASUNTO */}
        <TextField
          fullWidth
          size="small"
          label="Asunto"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* SELECT FIRMA */}
        <Select
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          value={selectedSignature}
          onChange={(e) => setSelectedSignature(e.target.value)}
        >
          <MenuItem value="reply">Firma de respuesta</MenuItem>
          <MenuItem value="main">Firma principal</MenuItem>
          <MenuItem value="none">Sin firma</MenuItem>
        </Select>

        {/* BOTÓN ADJUNTAR */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<AttachFileIcon />}
          component="label"
          sx={{ mb: 1, ...accentOutlinedSx }}
        >
          Adjuntar archivos
          <input type="file" hidden multiple onChange={handleFileChange} />
        </Button>

        {/* Adjuntos nuevos */}
        {attachments.length > 0 && (
          <Box sx={{ mt: 1, mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {attachments.map((file) => (
              <Chip
                key={file.name}
                label={file.name}
                onDelete={() => removeAttachment(file.name)}
              />
            ))}
          </Box>
        )}

        {/* Respuesta editable (firma + texto nuevo) */}
        <DropZoneWrapper
          onFiles={(files) => setAttachments((prev) => [...prev, ...files])}
        >
          <TipTapEditorContestar
            editorRef={editorRef}
            initialHtml={initialHtml}
            onChange={() => {}}
          />
        </DropZoneWrapper>

        {/* Mensaje original citado intacto */}
        {quotedHtml ? (
          <Box
            sx={{
              mt: 2,
              border: "1px solid #e2e8f0",
              borderRadius: 1,
              p: 2,
              bgcolor: "#fafafa",
              maxHeight: 420,
              overflow: "auto",
              fontSize: 14,
              "& table": { borderCollapse: "collapse", width: "100%" },
              "& img": { maxWidth: "100%", height: "auto" },
            }}
            dangerouslySetInnerHTML={{ __html: quotedHtml }}
          />
        ) : loadingOriginal ? (
          <Typography sx={{ color: "#777", mt: 2 }}>
            Cargando mensaje original…
          </Typography>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={requestClose} disabled={isSending} sx={accentTextSx}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={sendReply}
          disabled={isSending}
          startIcon={
            isSending ? <CircularProgress size={16} color="inherit" /> : null
          }
          sx={accentBtnSx}
        >
          {isSending ? "Enviando..." : "Enviar respuesta"}
        </Button>
      </DialogActions>
    </Dialog>

    <ComposeDiscardConfirmDialog
      open={discardOpen}
      onCancel={() => setDiscardOpen(false)}
      onConfirm={confirmDiscard}
    />
    </>
  );
}
